import { useState } from "react";
import { Button, useSubmittingMutation } from "@/design-system/components/ui";
import type { PortalBatchResult } from "../api/batch";

// SP-2b — shared checkbox batch-approve UX for the TPS queues (Bills,
// Distribution Ledger, Tax forms). One selection Set + confirm dialog +
// a RESULT BANNER (not just a toast); per-id failures render inline under
// their rows so a partial success is never silent. The three tabs share
// this so their selection behaviour stays identical; the single-item
// approve flows are untouched.

export interface BatchBanner {
  tone: "success" | "warning";
  text: string;
}

export interface BatchApproveState {
  selected: Set<string>;
  toggle: (id: string) => void;
  setAll: (ids: string[], checked: boolean) => void;
  /** Clears selection, per-row errors, and the banner (e.g. on a filter change). */
  reset: () => void;
  /** id → error for rows the last batch could not approve. */
  rowErrors: Map<string, string>;
  banner: BatchBanner | null;
  dismissBanner: () => void;
  pending: boolean;
  confirmOpen: boolean;
  openConfirm: () => void;
  closeConfirm: () => void;
  submit: () => void;
}

export function useBatchApprove({
  approve,
  refresh,
  successText,
  onRequestError,
}: {
  approve: (ids: string[]) => Promise<PortalBatchResult>;
  /** The list loader the caller owns — awaited before the banner shows. */
  refresh: () => Promise<unknown>;
  /** Banner copy when EVERY selected item succeeded. */
  successText: (succeeded: number) => string;
  /** The whole request failed (network / 4xx) — nothing was approved. */
  onRequestError: (err: unknown) => void;
}): BatchApproveState {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Map<string, string>>(new Map());
  const [banner, setBanner] = useState<BatchBanner | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useSubmittingMutation((ids: string[]) => approve(ids), {
    refresh,
    onSuccess: (result) => {
      const errors = new Map<string, string>();
      for (const item of result.results) {
        if (!item.ok) errors.set(item.id, item.error ?? "Couldn't approve this item.");
      }
      setRowErrors(errors);
      setBanner(
        result.failed === 0
          ? { tone: "success", text: successText(result.succeeded) }
          : {
              tone: "warning",
              text: `${result.succeeded} approved, ${result.failed} need attention.`,
            },
      );
      setSelected(new Set());
      setConfirmOpen(false);
    },
    onError: (err) => {
      setConfirmOpen(false);
      onRequestError(err);
    },
  });

  return {
    selected,
    toggle: (id) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    setAll: (ids, checked) => setSelected(checked ? new Set(ids) : new Set()),
    reset: () => {
      setSelected(new Set());
      setRowErrors(new Map());
      setBanner(null);
    },
    rowErrors,
    banner,
    dismissBanner: () => setBanner(null),
    pending: mutation.pending,
    confirmOpen,
    openConfirm: () => setConfirmOpen(true),
    closeConfirm: () => setConfirmOpen(false),
    submit: () => {
      void mutation.submit([...selected]);
    },
  };
}

/** Select-all header + the "{n} selected" toolbar with the primary action.
 *  Renders nothing when there are no selectable rows. */
export function BatchSelectionHeader({
  batch,
  selectableIds,
  selectAllLabel,
  actionLabel,
  testIdPrefix,
}: {
  batch: BatchApproveState;
  selectableIds: string[];
  selectAllLabel: string;
  actionLabel: string;
  testIdPrefix: string;
}) {
  if (selectableIds.length === 0) return null;
  const allSelected = selectableIds.every((id) => batch.selected.has(id));
  const count = batch.selected.size;
  return (
    <div className="flex flex-wrap items-center justify-between gap-space-2">
      <label className="flex items-center gap-space-2 text-small text-onyx">
        <input
          type="checkbox"
          className="h-4 w-4 accent-gold"
          checked={allSelected}
          onChange={(e) => batch.setAll(selectableIds, e.target.checked)}
          data-testid={`${testIdPrefix}-select-all`}
        />
        {selectAllLabel}
      </label>
      {count > 0 && (
        <div className="flex flex-wrap items-center gap-space-2">
          <span className="text-small text-gray-mid" data-testid={`${testIdPrefix}-count`}>
            {count} selected
          </span>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={batch.openConfirm}
            disabled={batch.pending}
            data-testid={`${testIdPrefix}-action`}
          >
            {actionLabel}
          </Button>
        </div>
      )}
    </div>
  );
}

/** Per-row selection checkbox — render only beside approvable rows. */
export function BatchRowCheckbox({
  id,
  batch,
  label,
  testIdPrefix,
}: {
  id: string;
  batch: BatchApproveState;
  label: string;
  testIdPrefix: string;
}) {
  return (
    <input
      type="checkbox"
      className="mt-space-4 h-4 w-4 shrink-0 accent-gold"
      checked={batch.selected.has(id)}
      onChange={() => batch.toggle(id)}
      disabled={batch.pending}
      aria-label={label}
      data-testid={`${testIdPrefix}-select-${id}`}
    />
  );
}

/** Inline warning under a row the last batch couldn't approve. */
export function BatchRowError({ error, testId }: { error?: string; testId: string }) {
  if (!error) return null;
  return (
    <p
      role="alert"
      data-testid={testId}
      className="m-0 rounded-md border border-status-warning bg-status-warning/10 p-space-2 text-caption text-onyx"
    >
      {error}
    </p>
  );
}

/** The batch outcome banner — success-only vs partial ("need attention"). */
export function BatchResultBanner({
  batch,
  testIdPrefix,
}: {
  batch: BatchApproveState;
  testIdPrefix: string;
}) {
  if (!batch.banner) return null;
  const success = batch.banner.tone === "success";
  return (
    <div
      role="status"
      data-testid={`${testIdPrefix}-banner`}
      className={`flex items-start justify-between gap-space-2 rounded-md border p-space-3 text-small text-onyx ${
        success
          ? "border-status-success bg-status-success/10"
          : "border-status-warning bg-status-warning/10"
      }`}
    >
      <span>{batch.banner.text}</span>
      <Button type="button" variant="text" size="sm" onClick={batch.dismissBanner}>
        Dismiss
      </Button>
    </div>
  );
}

/** Confirm dialog for the batch action. */
export function BatchConfirmDialog({
  batch,
  title,
  body,
  confirmLabel,
  testIdPrefix,
}: {
  batch: BatchApproveState;
  title: string;
  body: string;
  confirmLabel: string;
  testIdPrefix: string;
}) {
  if (!batch.confirmOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-onyx/50 p-space-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid={`${testIdPrefix}-confirm-dialog`}
        className="w-full max-w-md rounded-md border-t-[2px] border-t-gold bg-white-pure p-space-5 shadow-lg"
      >
        <h2 className="m-0 font-display text-h3 font-bold text-onyx">{title}</h2>
        <p className="mt-space-2 text-small text-gray-mid">{body}</p>
        <div className="mt-space-4 flex flex-wrap items-center justify-end gap-space-2">
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={batch.closeConfirm}
            disabled={batch.pending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={batch.submit}
            loading={batch.pending}
            disabled={batch.pending || batch.selected.size === 0}
            data-testid={`${testIdPrefix}-confirm`}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
